/**
 * BirthdayPicker - Date picker component using three select fields
 * 
 * Integrates with React Hook Form via Controller.
 * Generates month, day, and year options based on age constraints.
 * 
 * @example
 * ```tsx
 * <Controller
 *   name="birthday"
 *   control={control}
 *   render={({ field }) => (
 *     <BirthdayPicker
 *       value={field.value}
 *       onChange={(val) => {
 *         field.onChange(val);
 *         field.onBlur();
 *       }}
 *       error={errors.birthday}
 *     />
 *   )}
 * />
 * ```
 */

"use client";

import { Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { useMemo } from "react";
import { Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";
import { buildYearSelectOptions } from "@/lib/utils/date/year-select-options";
import { getErrorMessage } from "@/lib/utils/form";
import { CustomSelect } from "./CustomSelect";

export interface BirthdayValue {
  day: string;
  month: string;
  year: string;
}

export interface BirthdayPickerProps {
  /**
   * Current birthday value
   */
  value: BirthdayValue;
  /**
   * Change handler
   */
  onChange: (value: BirthdayValue) => void;
  /**
   * Error message
   */
  error?: { message?: string };
  /**
   * Whether field is required
   */
  required?: boolean;
  /**
   * Help text
   */
  help?: string;
  /**
   * Minimum age (default: 0)
   */
  minAge?: number;
  /**
   * Maximum age (default: 100)
   */
  maxAge?: number;
}

const MONTHS_IN_YEAR = 12;
const DAYS_IN_MONTH = 31;

export const BirthdayPicker = ({
  value,
  onChange,
  error,
  required = false,
  help,
  minAge = 0,
  maxAge = 100,
}: BirthdayPickerProps) => {
  const theme = useThemeTokens();
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: MONTHS_IN_YEAR }, (_, i) => ({
        label: String(i + 1).padStart(2, "0"),
        value: String(i + 1),
      })),
    [],
  );

  const dayOptions = useMemo(
    () =>
      Array.from({ length: DAYS_IN_MONTH }, (_, i) => ({
        label: String(i + 1).padStart(2, "0"),
        value: String(i + 1),
      })),
    [],
  );

  const yearOptions = useMemo(
    () =>
      buildYearSelectOptions({
        minYear: currentYear - maxAge,
        maxYear: currentYear - minAge,
        order: "desc",
        includeYear: value?.year,
      }),
    [currentYear, maxAge, minAge, value?.year],
  );

  const handleChange = (part: "day" | "month" | "year", val: string) => {
    onChange({
      ...value,
      [part]: val,
    });
  };

  const errorMessage = getErrorMessage(error, "Birthday is required", ["day", "month", "year"]);
  const hasError = !!error;

  return (
    <FormControl isInvalid={hasError} w="100%" minW={0}>
      <FormLabel fontSize="sm" fontWeight="500" mb="5px" color={theme.text.primary} letterSpacing="-0.01em">
        Birthday
        {required && (
          <Text as="span" color={theme.accent.primary} ml={1}>
            *
          </Text>
        )}
      </FormLabel>

      <Flex gap={2} w="100%" minW={0}>
        <CustomSelect
          options={monthOptions}
          placeholder="MM"
          value={value.month || ""}
          onChange={(val) => handleChange("month", val)}
          formControlProps={{
            flex: "1 1 0",
            w: "auto",
            minW: 0,
            isInvalid: hasError,
          }}
          data-testid="birthday-month"
        />
        <CustomSelect
          options={dayOptions}
          placeholder="DD"
          value={value.day || ""}
          onChange={(val) => handleChange("day", val)}
          formControlProps={{
            flex: "1 1 0",
            w: "auto",
            minW: 0,
            isInvalid: hasError,
          }}
          data-testid="birthday-day"
        />
        <CustomSelect
          options={yearOptions}
          placeholder="YYYY"
          value={value.year || ""}
          onChange={(val) => handleChange("year", val)}
          formControlProps={{
            flex: "1.25 1 0",
            w: "auto",
            minW: 0,
            isInvalid: hasError,
          }}
          data-testid="birthday-year"
        />
      </Flex>

      {errorMessage && (
        <Text color={theme.accent.primary} fontSize="xs" fontWeight="500" mt={1.5}>
          {errorMessage}
        </Text>
      )}

      {help && (
        <Text variant="tertiary" fontSize="xs" mt={errorMessage ? 1 : 1.5}>
          {help}
        </Text>
      )}
    </FormControl>
  );
};

